'use client';
import { createContext, ReactNode, Dispatch } from 'react';
import { BrokerAction } from '../ContextBroker/ContextBroker';
import { useContextBroker } from '../ContextBroker/useContextBroker';
import { noop } from '@/util/misc';
import {
  INITIAL_SIGNUP_VALUE,
  SignupBrokeredValue,
  SignupState
} from '@/types/Signup';

export const requiredSignupFields: (keyof SignupState)[] = [
  'email',
  'phone',
  'password',
  'firstName',
  'lastName',
  'birthday',
  'address',
  'city',
  'state',
  'postalCode',
  'creditCardLast4',
  'expirationDate',
  'stripePaymentMethodTokenId',
  'stripePaymentMethodBrand',
  'addon_subscription',
  'insurance_level'
];

export const SignupContext = createContext<SignupBrokeredValue>({
  value: { ...INITIAL_SIGNUP_VALUE },
  actions: {
    updateFormValues: noop,
    resetForm: noop,
    formIsComplete: noop
  }
});

type SignupProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function SignupProvider(props: SignupProviderProps) {
  const { children, dispatch } = props;
  const brokerValue = useContextBroker();

  const decoratedValue: SignupBrokeredValue = {
    value: brokerValue.signup,
    actions: {
      updateFormValues: (formValues) => {
        dispatch({
          type: 'SIGNUP_PARTIAL_UPDATE',
          payload: formValues
        });
      },
      resetForm: () => {
        dispatch({
          type: 'SIGNUP_CLEAR'
        });
      },
      formIsComplete: () => {
        let isComplete = true;
        for (let field of requiredSignupFields) {
          const fieldName = field as keyof SignupState;
          if (
            !brokerValue?.signup ||
            !brokerValue.signup[fieldName] ||
            brokerValue.signup[fieldName] === ''
          ) {
            isComplete = false;
            break;
          }
        }

        return isComplete;
      }
    }
  };

  return (
    <SignupContext.Provider value={decoratedValue}>
      {children}
    </SignupContext.Provider>
  );
}

export default SignupProvider;
